import {
  BILLING_PERIODS_PER_YEAR,
  type BillingPeriod,
  SUBSCRIPTION_TIER_RANK,
  type SubscriptionTier,
} from '@pulsify/shared/dto'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Check, Info, Loader2, Minus } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { usePlan } from '~/app/plan-context'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useOrganizationProfiles } from '~/entities/profile/model/use-profiles'
import type { StripePrice, StripeProduct } from '~/entities/subscription/api'
import {
  useCreateCheckout,
  usePricing,
  useResumeSubscription,
  useSyncPlansOnReturn,
} from '~/entities/subscription/model/use-stripe'
import {
  BILLING_PERIOD_RANK,
  billingPeriodLabel,
  billingPeriodSuffix,
  CHEAPER_BILLING_PERIODS,
  formatPrice,
  getFeatureCategories,
  getPlanHighlights,
  getPlans,
  LARGE_CUSTOMER_EMAIL,
  managedPlanProfileId,
  offeredPlans,
  planName,
  yearlySavingsLabel,
} from '~/entities/subscription/plans'
import { BillingPeriodSwitch } from '~/entities/subscription/ui/billing-period-switch'
import { PendingChangeAlert } from '~/entities/subscription/ui/pending-change-alert'
import { BillingOverview } from '~/features/billing/billing-overview'
import { InvoicesPanel } from '~/features/billing/invoices-panel'
import { ProfileBillingCard } from '~/features/billing/profile-billing-card'
import { UpgradeSuccessDialog } from '~/features/settings/upgrade-success-dialog'
import * as m from '~/paraglide/messages'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'
import { cn } from '~/shared/lib/utils'
import { Alert, AlertDescription, AlertTitle } from '~/shared/ui/alert'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/ui/dialog'
import { Disclosure } from '~/shared/ui/disclosure'
import { Skeleton } from '~/shared/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/shared/ui/tooltip'
import { PageHeader } from '~/widgets/page-header'

export const Route = createFileRoute('/dashboard/subscription')({
  validateSearch: (search: Record<string, unknown>) => ({
    success: search.success === 'true',
    canceled: search.canceled === 'true',
    tab: search.tab === 'invoices' ? ('invoices' as const) : undefined,
  }),
  component: SubscriptionPage,
})

const POST_CHECKOUT_REFRESH_DELAYS = [1500, 4000, 8000]

function findPrice(
  products: StripeProduct[],
  tier: SubscriptionTier,
  period: BillingPeriod,
): StripePrice | null {
  const product = products.find((p) => p.type.toLowerCase() === tier)
  return product?.prices.find((p) => p.billingPeriod === period) ?? null
}

function SubscriptionPage() {
  const { success, canceled, tab } = Route.useSearch()
  const queryClient = useQueryClient()
  const { data: business } = useCurrentBusiness()
  const businessId =
    storageManager.getAdminViewingBusinessId() || business?.id || storageManager.getBusinessId()
  useSyncPlansOnReturn(businessId, success)
  const pendingTierRef = useRef<SubscriptionTier>('free')
  const pendingIsDowngradeRef = useRef(false)
  const { mutate: createCheckout, isPending: isCheckoutLoading } = useCreateCheckout(() => {
    // A downgrade only schedules a change, so the upgrade dialog would celebrate something
    // that has not happened yet. The pending-change alert reports it with the real date.
    if (pendingIsDowngradeRef.current) {
      toast.success(m.planChangeScheduled())
      return
    }
    setUpgradedTier(pendingTierRef.current)
    setShowUpgradeSuccess(true)
  })
  const {
    tier: currentTier,
    isTrial,
    trialEndsAt,
    pendingChange,
    billingPeriod: currentBillingPeriod,
    ownPlan,
    billingEntityId,
  } = usePlan()
  const profiles = useOrganizationProfiles(businessId)
  const activeProfile = profiles.enabled ? profiles.active : null
  const invoiceProfileId = managedPlanProfileId(activeProfile?.id, ownPlan)
  const profileId = activeProfile?.id
  const [payerId, setPayerId] = useState<string | null>(null)
  const { mutate: revertScheduledChange, isPending: isReverting } = useResumeSubscription(
    businessId ?? undefined,
    managedPlanProfileId(profileId, ownPlan),
  )
  const { data: pricing, isLoading: isPricingLoading } = usePricing()

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('quarterly')
  const [pendingUpgrade, setPendingUpgrade] = useState<{
    tier: SubscriptionTier
    priceId: string
    displayPrice: string
    billingPeriod: BillingPeriod
    isDowngrade: boolean
  } | null>(null)
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false)
  const [upgradedTier, setUpgradedTier] = useState<SubscriptionTier>('free')
  const plansHeadingId = useId()
  const plans = useMemo(() => getPlans(), [])
  const featureCategories = useMemo(() => getFeatureCategories(), [])

  useEffect(() => {
    if (!success && !canceled) return

    window.history.replaceState({}, '', '/dashboard/subscription')
    if (!success) return

    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.SUBSCRIPTION) })
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.CREDITS_BALANCE) })
    }

    // Stripe delivers checkout.session.completed while this page is still
    // booting, so a single refetch on mount can read the pre-checkout tier and
    // nothing would ever ask again. Re-read a few times across the window the
    // webhook normally lands in.
    refresh()
    const timers = POST_CHECKOUT_REFRESH_DELAYS.map((delay) => setTimeout(refresh, delay))
    return () => timers.forEach(clearTimeout)
  }, [success, canceled, queryClient])

  const priceFor = (tier: SubscriptionTier, period: BillingPeriod) =>
    pricing ? findPrice(pricing.products, tier, period) : null

  const visiblePlans = offeredPlans(
    plans,
    pricing ? pricing.products.map((product) => product.type.toLowerCase()) : null,
    currentTier,
  )

  // A longer commitment bills at once like an upgrade; anything else - including a period
  // we cannot rank, such as a legacy price - is scheduled. Mirrors periodChangeKind().
  const isScheduledChange = (tier: SubscriptionTier) =>
    tier === currentTier
      ? !(
          currentBillingPeriod &&
          BILLING_PERIOD_RANK[billingPeriod] > BILLING_PERIOD_RANK[currentBillingPeriod]
        )
      : SUBSCRIPTION_TIER_RANK[tier] < SUBSCRIPTION_TIER_RANK[currentTier]

  const handlePlanChange = (tier: SubscriptionTier) => {
    if (tier === 'free') return
    if (!businessId) return

    const price = priceFor(tier, billingPeriod)
    if (!price) return

    if (currentTier === 'free') {
      pendingTierRef.current = tier
      pendingIsDowngradeRef.current = false
      createCheckout({
        businessId,
        profileId,
        billingEntityId: payerId ?? undefined,
        priceId: price.priceId,
        successUrl: `${window.location.origin}/dashboard/subscription?success=true`,
        cancelUrl: `${window.location.origin}/dashboard/subscription?canceled=true`,
      })
    } else {
      setPendingUpgrade({
        tier,
        priceId: price.priceId,
        displayPrice: formatPrice(price.unitAmount, price.currency),
        billingPeriod,
        isDowngrade: isScheduledChange(tier),
      })
    }
  }

  const handleConfirmUpgrade = () => {
    if (!pendingUpgrade || !businessId) return
    pendingTierRef.current = pendingUpgrade.tier
    pendingIsDowngradeRef.current = pendingUpgrade.isDowngrade
    setPendingUpgrade(null)
    createCheckout({
      businessId,
      profileId,
      priceId: pendingUpgrade.priceId,
      successUrl: `${window.location.origin}/dashboard/subscription?success=true`,
      cancelUrl: `${window.location.origin}/dashboard/subscription?canceled=true`,
    })
  }

  // One row per cheaper period regardless of whether it saves anything, so every
  // plan card reserves the same height and the feature lists stay aligned.
  const yearlySavings = (tier: SubscriptionTier) => {
    const selected = priceFor(tier, billingPeriod)
    const selectedPerYear = selected
      ? selected.unitAmount * BILLING_PERIODS_PER_YEAR[billingPeriod]
      : 0

    return CHEAPER_BILLING_PERIODS[billingPeriod].map((period) => {
      const price = selected && priceFor(tier, period)
      if (!price) return { period, label: null }

      const saved = selectedPerYear - price.unitAmount * BILLING_PERIODS_PER_YEAR[period]
      return {
        period,
        label: saved > 0 ? yearlySavingsLabel(period, formatPrice(saved, price.currency)) : null,
      }
    })
  }

  const yearlyDiscount = Math.max(
    0,
    ...visiblePlans.map((plan) => {
      const monthly = priceFor(plan.tier, 'monthly')
      const yearly = priceFor(plan.tier, 'yearly')
      return monthly && yearly
        ? Math.floor((1 - yearly.unitAmount / (monthly.unitAmount * 12)) * 100)
        : 0
    }),
  )

  return (
    <div className="space-y-8">
      <PageHeader title={m.subscription()} description={m.chooseThePlanForYourBusiness()} />
      <nav className="flex gap-1 border-b" aria-label={m.subscription()}>
        <PlanTab active={tab !== 'invoices'} search={{}} label={m.planTabPlan()} />
        <PlanTab
          active={tab === 'invoices'}
          search={{ tab: 'invoices' }}
          label={m.planTabInvoices()}
        />
      </nav>
      {tab === 'invoices' && businessId ? (
        <InvoicesPanel businessId={businessId} portalProfileId={invoiceProfileId} />
      ) : (
        <>
          {businessId ? (
            <div className="space-y-3">
              <BillingOverview
                businessId={businessId}
                tier={currentTier}
                billingPeriod={currentBillingPeriod ?? null}
              />
              {activeProfile ? (
                <ProfileBillingCard
                  businessId={businessId}
                  profileName={activeProfile.name}
                  ownPlan={ownPlan}
                  billingEntityId={billingEntityId}
                  selectedEntityId={payerId}
                  onSelectEntity={setPayerId}
                />
              ) : null}
            </div>
          ) : null}

          {isTrial && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>{m.trialBannerTitle()}</AlertTitle>
              <AlertDescription>
                {m.trialBannerDescription({
                  tier: planName(currentTier),
                  date: trialEndsAt
                    ? new Intl.DateTimeFormat(undefined, {
                        dateStyle: 'medium',
                      }).format(new Date(trialEndsAt))
                    : '-',
                })}
              </AlertDescription>
            </Alert>
          )}

          {pendingChange && (
            <PendingChangeAlert
              pendingChange={pendingChange}
              onRevert={() => revertScheduledChange()}
              isReverting={isReverting}
            />
          )}

          <section aria-labelledby={plansHeadingId} className="space-y-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <h2 id={plansHeadingId} className="font-semibold text-xl tracking-[-0.02em]">
                {m.planChoose()}
              </h2>
              <BillingPeriodSwitch value={billingPeriod} onChange={setBillingPeriod} />
              {yearlyDiscount > 0 ? (
                <p className="text-muted-foreground text-xs">
                  {m.planYearlySavingsHint({ percent: String(yearlyDiscount) })}
                </p>
              ) : null}
            </div>

            <div
              className={cn(
                'grid gap-4 pt-2',
                visiblePlans.length > 4
                  ? 'sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5'
                  : visiblePlans.length === 4
                    ? 'sm:grid-cols-2 xl:grid-cols-4'
                    : 'sm:grid-cols-2 lg:grid-cols-3',
              )}
            >
              {visiblePlans.map((plan) => {
                const isCurrentTier = plan.tier === currentTier
                // Staying on the tier but picking another period is a real change, so the card
                // for the current plan is only inert when the period matches too.
                const isPeriodChange =
                  isCurrentTier && plan.tier !== 'free' && currentBillingPeriod !== billingPeriod
                const isCurrentPlan = isCurrentTier && !isPeriodChange
                const isDowngrade =
                  SUBSCRIPTION_TIER_RANK[plan.tier] < SUBSCRIPTION_TIER_RANK[currentTier]
                // Dropping to free is a cancellation, which lives in Settings with its own
                // confirmation; and validatePlanChange refuses any same-or-lower tier on a trial.
                const isDowngradeBlocked = isDowngrade && (plan.tier === 'free' || isTrial)
                const isHighlighted = plan.highlighted && !isCurrentPlan
                const stripePrice = plan.tier === 'free' ? null : priceFor(plan.tier, billingPeriod)
                const saving = yearlySavings(plan.tier).find(({ label }) => label)?.label
                const isUnavailable = plan.tier !== 'free' && !isPricingLoading && !stripePrice
                const displayPrice =
                  plan.tier === 'free'
                    ? formatPrice(0, 'eur')
                    : stripePrice
                      ? formatPrice(stripePrice.unitAmount, stripePrice.currency)
                      : m.priceUnavailable()

                return (
                  <Card
                    key={plan.tier}
                    className={cn(
                      'relative row-span-4 grid min-w-0 grid-rows-subgrid gap-5 p-6',
                      isHighlighted &&
                        'border-transparent shadow-[0_24px_48px_-24px_rgb(10_10_11/0.3)] ring-2 ring-brand',
                    )}
                  >
                    {isHighlighted ? (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-foreground px-3 py-1 font-semibold text-background text-xs">
                        {m.recommended()}
                      </span>
                    ) : null}
                    <div className="space-y-1">
                      <h3 className="font-semibold text-base">{plan.name}</h3>
                      <p className="text-muted-foreground text-sm">{plan.description}</p>
                    </div>

                    <div>
                      {isPricingLoading && plan.tier !== 'free' ? (
                        <Skeleton className="h-10 w-28" />
                      ) : (
                        <p className="flex flex-wrap items-baseline gap-x-1">
                          <span
                            className={cn(
                              'font-semibold tabular-nums tracking-[-0.03em]',
                              isUnavailable ? 'text-base text-muted-foreground' : 'text-4xl',
                            )}
                          >
                            {displayPrice}
                          </span>
                          {stripePrice && (
                            <span className="text-muted-foreground text-sm">
                              {billingPeriodSuffix(billingPeriod)}
                            </span>
                          )}
                        </p>
                      )}
                      <p className="mt-1.5 min-h-8 text-muted-foreground text-xs">{saving}</p>
                    </div>

                    {isCurrentPlan ? (
                      <p className="squircle flex min-h-9 items-center justify-center gap-1.5 rounded-md bg-muted font-medium text-muted-foreground text-sm">
                        <Check className="size-4" />
                        {m.currentPlan()}
                      </p>
                    ) : plan.tier === 'free' && isDowngradeBlocked ? (
                      <Button asChild variant="ghost" size="sm" className="min-h-9 w-full">
                        <Link to="/dashboard/settings">{m.planCancelInSettings()}</Link>
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="h-auto min-h-9 w-full whitespace-normal"
                        variant={isHighlighted ? 'brand' : isDowngrade ? 'outline' : 'default'}
                        disabled={
                          !businessId ||
                          isDowngradeBlocked ||
                          isCheckoutLoading ||
                          (plan.tier !== 'free' && !stripePrice)
                        }
                        onClick={() => handlePlanChange(plan.tier)}
                      >
                        {isCheckoutLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : isDowngradeBlocked ? (
                          m.downgradeNotAvailable()
                        ) : isPeriodChange ? (
                          m.switchToPeriod({ period: billingPeriodLabel(billingPeriod) })
                        ) : isDowngrade ? (
                          m.switchToPlan({ tier: plan.name })
                        ) : (
                          m.upgradeToPlan({ tier: plan.name })
                        )}
                      </Button>
                    )}

                    <ul className="space-y-2.5 text-sm">
                      {getPlanHighlights(plan.tier).map((line) => (
                        <li key={line} className="flex items-start gap-2.5">
                          <span className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full bg-brand-subtle text-brand-subtle-foreground">
                            <Check className="size-3" strokeWidth={3} />
                          </span>
                          <span className="min-w-0">{line}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                )
              })}
            </div>

            <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-muted-foreground text-xs">
              <span>{m.planPricesNet()}</span>
              <span aria-hidden="true">·</span>
              <span>{m.planFreeTickets()}</span>
              <span aria-hidden="true">·</span>
              <span>
                {m.planLargeCustomers()}{' '}
                <a
                  href={`mailto:${LARGE_CUSTOMER_EMAIL}`}
                  className="font-medium text-brand-subtle-foreground underline-offset-2 hover:underline"
                >
                  {m.planPriceOnRequest()}
                </a>
              </span>
            </p>
          </section>

          <Card className="px-5 py-1">
            <Disclosure title={m.compareAllFeatures()} contentClassName="pb-4">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[44rem] text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground text-xs">
                      <th className="py-2 font-normal" />
                      {visiblePlans.map((plan) => (
                        <th key={plan.tier} className="w-[15%] py-2 font-medium">
                          {plan.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  {featureCategories.map((category) => (
                    <tbody key={category.category} className="border-border border-t">
                      <tr>
                        <td
                          colSpan={visiblePlans.length + 1}
                          className="pt-3 pb-1 font-medium text-muted-foreground text-xs"
                        >
                          {category.category}
                        </td>
                      </tr>
                      {category.features.map((feature) => (
                        <tr key={feature.name}>
                          <td className="py-1.5 pr-4">{feature.name}</td>
                          {visiblePlans.map((plan) => {
                            const value = feature.values[plan.tier]
                            return (
                              <td key={plan.tier} className="py-1.5 pr-2">
                                {value === true ? (
                                  <Check className="size-4 text-brand" />
                                ) : value === false ? (
                                  <Minus className="size-4 text-muted-foreground/50" />
                                ) : (
                                  value
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  ))}
                </table>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="mt-4 inline-flex cursor-help items-center gap-1 text-muted-foreground text-xs underline-offset-2 hover:underline"
                  >
                    <Info className="size-3.5" />
                    {m.earlyBirdInfoBadge()}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  {m.earlyBirdInfoDescription()}
                </TooltipContent>
              </Tooltip>
            </Disclosure>
          </Card>

          <UpgradeSuccessDialog
            open={showUpgradeSuccess}
            onOpenChange={setShowUpgradeSuccess}
            tier={planName(upgradedTier)}
          />

          <Dialog
            open={!!pendingUpgrade}
            onOpenChange={(open) => {
              if (!open) setPendingUpgrade(null)
            }}
          >
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>
                  {pendingUpgrade?.isDowngrade ? m.confirmDowngrade() : m.confirmUpgrade()}
                </DialogTitle>
                <DialogDescription className="space-y-2">
                  {pendingUpgrade && (
                    <>
                      <span className="block">
                        {(pendingUpgrade.isDowngrade
                          ? m.confirmDowngradeDescription
                          : m.confirmUpgradeDescription)({
                          tier: planName(pendingUpgrade.tier),
                          price: pendingUpgrade.displayPrice,
                          period: billingPeriodSuffix(pendingUpgrade.billingPeriod),
                        })}
                      </span>
                      {!pendingUpgrade.isDowngrade && (
                        // A trial has no subscription to amend, so it goes through
                        // Checkout; every other immediate change bills the card on file.
                        <span className="block">
                          {isTrial ? m.confirmUpgradeCheckout() : m.confirmUpgradeProration()}
                        </span>
                      )}
                    </>
                  )}
                  {isTrial && pendingUpgrade && (
                    <span className="block text-amber-600 dark:text-amber-400 font-medium">
                      {m.trialUpgradeWarning({
                        tier: planName(currentTier),
                        targetTier: planName(pendingUpgrade.tier),
                      })}
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setPendingUpgrade(null)}
                  disabled={isCheckoutLoading}
                >
                  {m.cancel()}
                </Button>
                <Button onClick={handleConfirmUpgrade} disabled={isCheckoutLoading}>
                  {isCheckoutLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : pendingUpgrade?.isDowngrade ? (
                    m.confirmDowngrade()
                  ) : (
                    m.confirmUpgrade()
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  )
}

function PlanTab({
  active,
  search,
  label,
}: {
  active: boolean
  search: { tab?: 'invoices' }
  label: string
}) {
  return (
    <Link
      to="/dashboard/subscription"
      search={search}
      aria-current={active ? 'page' : undefined}
      className={cn(
        '-mb-px border-b-2 px-3 py-2 font-medium text-sm transition-colors',
        active
          ? 'border-foreground text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </Link>
  )
}
